import React, { useState } from 'react';
import { formatUnitPrice } from '../utils/formatPrice';
import './Sparkline.css';

const W = 120;
const H = 32;
const PAD = 4;

// Daily price line with a dashed reference (e.g. your average cost) and today's price as an
// end dot. Hover or arrow keys move a crosshair that reads out the day's price.
function Sparkline({ points = [], reference, current, label }) {
  const [active, setActive] = useState(null);
  if (points.length < 2) return <span className="text-faint">—</span>;

  const values = points.map(([, p]) => p).concat([reference, current].filter(v => v != null));
  const lo = Math.min(...values);
  const span = Math.max(...values) - lo || 1;
  // Spaced by date, so days without trades leave gaps instead of squashing the line
  const days = points.map(([d]) => Date.parse(d));
  const x = i => PAD + (days[i] - days[0]) / (days[days.length - 1] - days[0] || 1) * (W - 2 * PAD);
  const y = v => H - PAD - (v - lo) / span * (H - 2 * PAD);
  const path = points.map(([, p], i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p).toFixed(1)}`).join('');

  const pick = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width * W;
    setActive(days.reduce((best, _, i) => (Math.abs(x(i) - px) < Math.abs(x(best) - px) ? i : best), 0));
  };
  const step = e => {
    const delta = { ArrowLeft: -1, ArrowRight: 1 }[e.key];
    if (!delta) return;
    e.preventDefault();
    setActive(i => Math.min(points.length - 1, Math.max(0, (i ?? points.length - 1) + delta)));
  };

  const hovered = active != null && points[active];
  return (
    <span className="spark">
      <svg
        width={W} height={H} viewBox={`0 0 ${W} ${H}`} tabIndex={0} role="img" aria-label={label}
        onPointerMove={pick} onPointerLeave={() => setActive(null)}
        onFocus={() => setActive(points.length - 1)} onBlur={() => setActive(null)} onKeyDown={step}
      >
        {reference != null && (
          <line className="spark-ref" x1={PAD} x2={W - PAD} y1={y(reference)} y2={y(reference)} />
        )}
        <path className="spark-line" d={path} />
        {current != null && <circle className="spark-now" cx={W - PAD} cy={y(current)} r={2.5} />}
        {hovered && (
          <>
            <line className="spark-cross" x1={x(active)} x2={x(active)} y1={0} y2={H} />
            <circle className="spark-dot" cx={x(active)} cy={y(hovered[1])} r={2.5} />
          </>
        )}
      </svg>
      {hovered && (
        <span className="spark-tip" style={{ left: `${x(active) / W * 100}%` }}>
          <b>{formatUnitPrice(hovered[1])}</b>
          <span>{new Date(hovered[0]).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })}</span>
        </span>
      )}
    </span>
  );
}

export default Sparkline;
